// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import { Dictionary } from "@insite/client-framework/Common/Types";
import { IconPresentationProps } from "@insite/mobius/Icon";
import PinLine, { PinLineOffset } from "@store/Library/Components/Toolkit/Icons/Pin/PinLine";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { menuSecondaryNavigationItemStyles } from "@store/Themes/common/mixins/menu";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { LinkModelWithIcon } from "@toolkit/Framework/Store/Links/LinksSelectors";
import { IconOffset } from "@toolkit/Framework/Types/Icon";
import { iconScalableStyles } from "@toolkit/Themes/common/mixins";
/* Store Custom Code - Start */
import HeaderLinkListItem, { LinkIconPropsWithoutSrc } from "@store/Library/Components/Header/HeaderLinkListItem";

/* Store Custom Code - End */

export interface HeaderLinkListIcon {
    displayName: string;
    src: IconPresentationProps["src"];
    size?: string;
    offset?: IconOffset;
}
/* Store Custom Code */
interface LinkFieldsWithIconKey extends Omit<LinkModelWithIcon, "fields"> {
    fields: Omit<LinkModelWithIcon["fields"], "icon" & "position"> & {
        icon: string;
        position?: "right" | "left";
    };
}
export const headerLinkListIcons: Dictionary<HeaderLinkListIcon> = {
    location: {
        displayName: "Location",
        size: pxToRem(20), // Store Custom Code
        src: PinLine,
        offset: PinLineOffset,
    },
};
/* Starter Custom Code - End */

const enum fields {
    links = "links",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkFieldsWithIconKey[]; // Store Custom Code
        [fields.customCSS]: string;
    };
    extendedStyles?: HeaderLinkListStyles;
}

export interface HeaderLinkListStyles {
    headerLinkListWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    // Starter Custom Code
    linkWrapper?: InjectableCss;
}

export const headerLinkListStyles: HeaderLinkListStyles = {
    headerLinkListWrapper: {
        css: css`
            ${menuSecondaryNavigationItemStyles()}
            display: flex;
            align-items: center;
            height: 100%;
        `,
    },
    linkWrapper: {
        css: css`
            ${menuSecondaryNavigationItemStyles()}
            display: flex;
            align-items: center;
        `,
    },
    link: {
        icon: {
            iconProps: {
                color: "inherit",
            },
        },
        css: linkStyles("inherit", "menu.secondaryNavigation.hover", false, true),
    },
};

/* Starter Custom Code - Start */
const defaultCustomCss = `& {
}

.list-link {
}
`;
/* Starter Custom Code - End */

export const HeaderLinkList: React.FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { links, hasMissingCategories } = useGetLinks(fields.links, o => o.fields.destination);

    const styles: HeaderLinkListStyles = useMergeStyles("headerLinkList", headerLinkListStyles, extendedStyles);

    if (hasMissingCategories) {
        return null;
    }

    if (links.length < 1) {
        return null;
    }

    /* Starter Custom Code - Start */
    const customCssWrapper = css`
        ${styles.headerLinkListWrapper?.css}
        ${fields.customCSS}
    `;
    return (
        <StyledWrapper {...styles.headerLinkListWrapper} css={customCssWrapper}>
            {links.map((link, index) => {
                if (!link.url) {
                    return null;
                }

                const linkfields = fields.links[index].fields;

                const title = linkfields.overriddenTitle || link.title;
                const iconData = headerLinkListIcons[linkfields.icon] || {};
                const spacingSide = linkfields.iconPosition === "left" ? "right" : "left";
                // Store Custom Code - >
                const icon = linkfields.icon;
                const iconProps: LinkIconPropsWithoutSrc | undefined = mergeProps<LinkPresentationProps["icon"]>(
                    styles.link?.icon,
                    {
                        position: linkfields.iconPosition,
                        iconProps: {
                            css: css`
                                ${iconScalableStyles(iconData.size, iconData.offset)}
                                margin-${spacingSide}: ${x => x.theme.design.menu.secondaryNavigation.iconSpacing};
                            `,
                        },
                    },
                );

                return (
                    <StyledWrapper key={`${link.url}.${title}`} {...styles.linkWrapper}>
                        <HeaderLinkListItem
                            iconKey={icon}
                            iconPropsWithoutSource={iconProps}
                            href={link.url}
                            {...styles.link}
                            target={fields.links[index].fields.openInNewWindow ? "_blank" : "_self"}
                        >
                            {title}
                        </HeaderLinkListItem>
                    </StyledWrapper>
                    // <--- Bunzl Custom Code
                );
            })}
        </StyledWrapper>
    );
    /* Starter Custom Code - End */
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule<Bunzl.BunzlDefinition> = {
    component: HeaderLinkList,
    definition: {
        group: "Header",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Contextual", // Starter Custom Code
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    /* Starter Custom Code - Start */
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "icon",
                        editorTemplate: "IconPickerField",
                        defaultValue: "",
                    },
                    {
                        name: "iconPosition",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Left", value: "left" },
                            { displayName: "Right", value: "right" },
                        ],
                        defaultValue: "left",
                        isRequired: true,
                        isVisible: x => x.fields.icon,
                    },
                    /* Starter Custom Code - End */
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
